import type { ElementType } from "react";

import { FlowsIcon, ProjectsIcon, SettingsIcon } from "@hightouch/htui";

import {
  AUTOMATION_PATHS,
  AUTOMATIONS_PANEL,
  CHATS_PANEL,
  type CdpEntry,
  INSIGHTS_PANEL,
  LIBRARY_PANEL,
  PROJECTS_PANEL,
} from "src/components/layout/nav/amp-nav-panels";
import {
  NAV_ROOT_SECTIONS,
  placeInSections,
} from "src/components/layout/nav/amp-nav-layout";
import { Icon as ActivationIcon } from "src/components/layout/nav/icons/activation";
import { Icon as AdStudioIcon } from "src/components/layout/nav/icons/ad-studio";
import { Icon as AgentsIcon } from "src/components/layout/nav/icons/agents";
import { Icon as ContextIcon } from "src/components/layout/nav/icons/context";
import { Icon as CustomerStudioIcon } from "src/components/layout/nav/icons/customer-studio";
import { Icon as DecisionEngineIcon } from "src/components/layout/nav/icons/decision-engine";
import { Icon as EventCollectionIcon } from "src/components/layout/nav/icons/event-collection";
import { Icon as GetStartedIcon } from "src/components/layout/nav/icons/get-started";
import { Icon as HomeIcon } from "src/components/layout/nav/icons/home";
import { Icon as IdentityResolutionIcon } from "src/components/layout/nav/icons/identity-resolution";
import { Icon as IntegrationsIcon } from "src/components/layout/nav/icons/integrations";
import { Icon as IntelligenceIcon } from "src/components/layout/nav/icons/intelligence";
import { Icon as LifecycleStudioIcon } from "src/components/layout/nav/icons/lifecycle-studio";
import { Icon as MessagesIcon } from "src/components/layout/nav/icons/messages";
import type { NavSectionLink } from "src/components/layout/nav/nav-section";
import { useActivateSection } from "src/components/layout/nav/sections/activate";
import { useAdStudioSection } from "src/components/layout/nav/sections/ad-studio";
import { useAgentsSection } from "src/components/layout/nav/sections/agents";
import {
  AMP_INSIGHTS_PATH,
  AMP_NEW_AD_CHAT_PATH,
  type AmpNavLink,
  useAdStudioInAmpNav,
  useAmpNavLinks,
} from "src/components/layout/nav/sections/amp-nav-links";
import { useCEPSection } from "src/components/layout/nav/sections/cep";
import { useContextHubSection } from "src/components/layout/nav/sections/context-hub";
import { useCustomerStudioSection } from "src/components/layout/nav/sections/customer-studio";
import { useDecisionEnginesSection } from "src/components/layout/nav/sections/decision-engine";
import { useEventCollectionSection } from "src/components/layout/nav/sections/event-collection";
import { useGetStartedSection } from "src/components/layout/nav/sections/get-started";
import { useHomeSection } from "src/components/layout/nav/sections/home";
import {
  AGENT_CHATS_PATH,
  useIsAgentsOnlyWorkspace,
  useShowAgentsInNav,
} from "src/components/layout/nav/sections/hooks/agents";
import {
  useContextHomePath,
  useContextLinks,
} from "src/components/layout/nav/sections/hooks/context";
import { useIntelligenceSection } from "src/components/layout/nav/sections/hooks/intelligence";
import {
  useCanUseProductTabs,
  useHasProductTabs,
  useProjectsAccess,
} from "src/components/layout/nav/sections/hooks/projects";
import { useSettingsHomePath } from "src/components/layout/nav/sections/hooks/settings";
import { useIdentityResolutionSection } from "src/components/layout/nav/sections/identity-resolution";
import { useIntegrationsSection } from "src/components/layout/nav/sections/integrations";
import { useLifecycleStudioSection } from "src/components/layout/nav/sections/lifecycle-studio";
import { useSettingsPages } from "src/components/layout/nav/sections/settings-nav";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useSpaceContext } from "src/components/spaces";
import { useUser } from "src/contexts/user-context";
import { useHasOrganizationSettings } from "src/hooks/use-has-organization-settings";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { useIsAiDecisionEngineEnabled } from "src/pages/decision-engines/utils";
import {
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";

export type ReachableLink = { href: string; label: string };

type ReachablePages = {
  entries: ReachableEntry[];
  /** Pages a panel or an area's own nav lists, which the entries do not name. */
  panelHrefs: string[];
};

/** One entry of the nav the viewer is on: a page, or a section that opens its own links. */
export type ReachableEntry = ReachableLink & {
  key: string;
  icon: ElementType;
  links: ReachableLink[];
};

type Section = Omit<CdpEntry, "links"> & {
  isEnabled: boolean;
  links: Array<NavSectionLink | false>;
};

/** One of the AMP nav's root rows, before its sections place it. A `place` row stands for a section of AMP, lit on every page under it. */
export type AmpRootRow = { key: string; fallbackSection: number } & (
  | { kind: "page"; link: AmpNavLink }
  | { kind: "place"; link: AmpNavLink }
  | { kind: "projects" }
  | { kind: "automations"; first: AmpNavLink; links: AmpNavLink[] }
  | { kind: "cdp"; entry: CdpEntry; first: NavSectionLink }
);

/** The Projects row's pages: the overview and Tasks. */
export const PROJECTS_LINKS: ReachableLink[] = [
  { href: PROJECTS_PATHS.projects, label: "Overview" },
  { href: PROJECTS_PATHS.tasks, label: TERMS.task.Plural },
];

export const isAutomationLink = (link: AmpNavLink) =>
  AUTOMATION_PATHS.includes(link.href);

const HOME_PATH = "/home";
const WORKSPACE_SETTINGS_PATH = "/settings";

/** The AMP nav's root rows in its order: its lead pages, Projects, Content, its other pages, then the CDP's sections. */
export const ampRootRows = (
  {
    lead,
    area,
    more,
  }: Pick<ReturnType<typeof useAmpNavLinks>, "lead" | "area" | "more">,
  cdpEntries: CdpEntry[],
): AmpRootRow[] => {
  const library = area.find(({ href }) => href === PROJECTS_PATHS.libraryNew);
  const automations = more.filter(isAutomationLink);
  const ampRow = (link: AmpNavLink, place?: string): AmpRootRow =>
    place
      ? { kind: "place", key: place, link, fallbackSection: 0 }
      : { kind: "page", key: link.href, link, fallbackSection: 0 };
  return [
    ...lead.map((link) =>
      ampRow(link, link.href === AGENT_CHATS_PATH ? CHATS_PANEL : undefined),
    ),
    { kind: "projects", key: PROJECTS_PANEL, fallbackSection: 0 },
    ...(library ? [ampRow(library, LIBRARY_PANEL)] : []),
    // Routines, Actions and Launch share one Automations row, where the first of them sits.
    ...more.flatMap((link): AmpRootRow[] => {
      if (!isAutomationLink(link)) {
        return [
          ampRow(
            link,
            link.href === AMP_INSIGHTS_PATH ? INSIGHTS_PANEL : undefined,
          ),
        ];
      }
      return link === automations[0]
        ? [
            {
              kind: "automations",
              key: AUTOMATIONS_PANEL,
              first: link,
              links: automations,
              fallbackSection: 0,
            },
          ]
        : [];
    }),
    ...cdpEntries.flatMap((entry): AmpRootRow[] => {
      const [first] = entry.links;
      return first
        ? [
            {
              kind: "cdp",
              key: entry.title,
              entry,
              first,
              fallbackSection: NAV_ROOT_SECTIONS.length - 1,
            },
          ]
        : [];
    }),
  ];
};

/** Drops disabled sections and the links a space hides, then a section left with none. */
export const reachableSections = (
  sections: Array<Section | false>,
  isReachable: (href: string) => boolean,
): CdpEntry[] =>
  sections.flatMap((section) => {
    if (!section || !section.isEnabled) return [];
    const links = section.links
      .filter((link): link is NavSectionLink => Boolean(link))
      .filter((link) => isReachable(link.href));
    if (links.length === 0) return [];
    // A panel of one row is a detour, so a single-page section links straight to it.
    return [
      {
        title: section.title,
        icon: section.icon,
        links,
        isLink: Boolean(section.isLink) || links.length === 1,
      },
    ];
  });

const useIsReachableInSpace = () => {
  const { isInSpace, isPathOrChildrenAccessibleInSpace } = useSpaceContext();
  return (href: string) =>
    !isInSpace || isPathOrChildrenAccessibleInSpace(href);
};

/** The CDP's sections in the classic nav's order, without the ones this viewer cannot reach. */
export const useCdpEntries = (): CdpEntry[] => {
  const { resources } = useUser();
  const agentsOnly = useIsAgentsOnlyWorkspace();
  const isDecisionEngineEnabled = useIsAiDecisionEngineEnabled();
  const { isInSpace } = useSpaceContext();
  const isReachable = useIsReachableInSpace();
  const activate = useActivateSection();
  const customerStudio = useCustomerStudioSection();
  const decisionEngines = useDecisionEnginesSection();
  const cep = useCEPSection();
  const intelligence = useIntelligenceSection();
  const identityResolution = useIdentityResolutionSection();
  const integrations = useIntegrationsSection();
  const eventCollection = useEventCollectionSection();

  const hasDecisionEngine =
    Boolean(resources?.decisionEngine) && isDecisionEngineEnabled;
  // The agents-only workspace Customer Studio narrows to Schema for: Activation and the AI Decisioning upsell only show once it uses them.
  // Held false while entitlements load, so a workspace with audiences doesn't lose these entries for a moment.
  const isAgentsOnly = agentsOnly.isAgentsOnly && !agentsOnly.isLoading;
  const usesActivation = Boolean(resources?.sync || resources?.model);
  return reachableSections(
    [
      (!isAgentsOnly || usesActivation) && {
        ...activate,
        icon: ActivationIcon,
      },
      { ...customerStudio, icon: CustomerStudioIcon },
      // A space hides /ai: its routes are guarded but missing from the space route registry.
      !isInSpace &&
        (hasDecisionEngine
          ? { ...decisionEngines, icon: DecisionEngineIcon }
          : !isAgentsOnly && AI_DECISIONING_LINK),
      { ...cep, icon: MessagesIcon },
      { ...intelligence, icon: IntelligenceIcon },
      { ...identityResolution, icon: IdentityResolutionIcon, isLink: true },
      { ...integrations, icon: IntegrationsIcon },
      { ...eventCollection, icon: EventCollectionIcon },
    ],
    isReachable,
  );
};

const AI_DECISIONING_LINK: Section = {
  title: "AI Decisioning",
  isEnabled: true,
  isLink: true,
  icon: DecisionEngineIcon,
  links: [{ href: "/ai", label: "AI Decisioning" }],
};

const toEntry = ({ title, icon, links, isLink }: CdpEntry): ReachableEntry => ({
  key: title,
  label: title,
  icon,
  href: links[0]?.href ?? "",
  links: isLink ? [] : links.map(({ href, label }) => ({ href, label })),
});

/** Get started and Home, which lead both navs. */
const useLeadEntries = (): ReachableEntry[] => {
  const getStarted = useGetStartedSection();
  const home = useHomeSection();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  return [
    ...(getStarted.isEnabled
      ? [
          {
            key: "get-started",
            label: "Get started",
            icon: GetStartedIcon,
            href: "/onboarding",
            links: [],
          },
        ]
      : []),
    ...(home.isEnabled && (!isInSpace || isPathAccessibleInSpace(HOME_PATH))
      ? [
          {
            key: "home",
            label: "Home",
            icon: HomeIcon,
            href: HOME_PATH,
            links: [],
          },
        ]
      : []),
  ];
};

const useAmpEntries = (): ReachablePages => {
  const links = useAmpNavLinks();
  const { hasAdStudio } = useAdStudioInAmpNav();
  const isReachable = useIsReachableInSpace();
  const cdpEntries = useCdpEntries();
  const settingsHomePath = useSettingsHomePath();
  const contextHomePath = useContextHomePath();
  const context = useContextLinks();
  const settingsPages = useSettingsPages();

  const toReachable = (row: AmpRootRow): ReachableEntry | null => {
    if (row.kind === "cdp") return toEntry(row.entry);
    // The palette keeps a space's gate on AMP's own pages, which the nav lists either way.
    if (row.kind === "projects") {
      const projectLinks = PROJECTS_LINKS.filter(({ href }) =>
        isReachable(href),
      );
      const [first] = projectLinks;
      return first
        ? {
            key: row.key,
            label: TERMS.project.Plural,
            icon: ProjectsIcon,
            href: first.href,
            links: projectLinks,
          }
        : null;
    }
    if (row.kind === "automations") {
      const automationLinks = row.links
        .filter(({ href }) => isReachable(href))
        .map(({ href, label }) => ({ href, label }));
      const [first] = automationLinks;
      return first
        ? {
            key: row.key,
            label: "Automations",
            icon: FlowsIcon,
            href: first.href,
            links: automationLinks,
          }
        : null;
    }
    const { href, label, icon } = row.link;
    return isReachable(href)
      ? { key: row.key, label, icon, href, links: [] }
      : null;
  };
  const root = placeInSections(
    ampRootRows(links, cdpEntries),
    NAV_ROOT_SECTIONS,
  ).flatMap((row) => toReachable(row) ?? []);
  const contextLinks = [
    ...context.context,
    ...(context.templates ? [context.templates] : []),
    ...context.agents,
  ];
  const entries = [
    ...root,
    ...(contextHomePath
      ? [
          {
            key: "context",
            label: "Context",
            icon: ContextIcon,
            href: contextHomePath,
            links: contextLinks.map(({ href, label }) => ({ href, label })),
          },
        ]
      : []),
    ...(settingsHomePath
      ? [
          {
            key: "settings",
            label: "Settings",
            icon: SettingsIcon,
            href: settingsHomePath,
            links: settingsPages.map(({ href, label }) => ({ href, label })),
          },
        ]
      : []),
  ];
  const hasContent = root.some(({ key }) => key === LIBRARY_PANEL);
  // The Content panel lists the Library's files and Ad Studio's ads.
  return {
    entries,
    panelHrefs: [
      ...(hasContent ? [PROJECTS_PATHS.library] : []),
      ...(hasAdStudio ? [AMP_NEW_AD_CHAT_PATH] : []),
    ].filter(isReachable),
  };
};

/** The classic nav's sections, each under the gate its own nav component adds. */
const useClassicEntries = (): ReachablePages => {
  const canUseProductTabs = useCanUseProductTabs();
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const { resources } = useUser();
  const isDecisionEngineEnabled = useIsAiDecisionEngineEnabled();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  const isReachable = useIsReachableInSpace();
  const { isPermitted: canViewAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_view_agents" },
  });
  const { isPermitted: canConfigureAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const agents = useAgentsSection();
  const activate = useActivateSection();
  const customerStudio = useCustomerStudioSection();
  const lifecycleStudio = useLifecycleStudioSection();
  const adStudio = useAdStudioSection();
  const contextHub = useContextHubSection();
  const decisionEngines = useDecisionEnginesSection();
  const cep = useCEPSection();
  const intelligence = useIntelligenceSection();
  const identityResolution = useIdentityResolutionSection();
  const integrations = useIntegrationsSection();
  const eventCollection = useEventCollectionSection();
  const { hasAccess: hasOrganizationSettings } = useHasOrganizationSettings();

  const hasDecisionEngine =
    Boolean(resources?.decisionEngine) && isDecisionEngineEnabled;
  const sections = reachableSections(
    [
      showAgents &&
        (isAgentsEnabled
          ? { ...agents, icon: AgentsIcon }
          : {
              title: "Agents",
              isEnabled: true,
              icon: AgentsIcon,
              links: [{ href: "/agents", label: "Agents" }],
            }),
      { ...activate, icon: ActivationIcon },
      { ...customerStudio, icon: CustomerStudioIcon },
      canViewAgents && { ...lifecycleStudio, icon: LifecycleStudioIcon },
      canViewAgents && { ...adStudio, icon: AdStudioIcon },
      canConfigureAgents && { ...contextHub, icon: ContextIcon },
      !isInSpace &&
        (hasDecisionEngine
          ? { ...decisionEngines, icon: DecisionEngineIcon }
          : AI_DECISIONING_LINK),
      { ...cep, icon: MessagesIcon },
      { ...intelligence, icon: IntelligenceIcon },
      identityResolution.isEnabled &&
        (!isInSpace || isPathAccessibleInSpace("/idr")) && {
          ...identityResolution,
          icon: IdentityResolutionIcon,
          isLink: true,
        },
      { ...integrations, icon: IntegrationsIcon },
      { ...eventCollection, icon: EventCollectionIcon },
    ],
    isReachable,
  );
  const entries = [
    ...(canUseProductTabs && isReachable(PROJECTS_PATHS.projects)
      ? [
          {
            key: "projects",
            label: TERMS.area,
            icon: ProjectsIcon,
            href: PROJECTS_PATHS.projects,
            links: [],
          },
        ]
      : []),
    ...sections.map((section) => toEntry(section)),
    ...(!isInSpace || isPathAccessibleInSpace(WORKSPACE_SETTINGS_PATH)
      ? [
          {
            key: "settings",
            label: "Settings",
            icon: SettingsIcon,
            href: WORKSPACE_SETTINGS_PATH,
            links: hasOrganizationSettings
              ? [
                  { href: WORKSPACE_SETTINGS_PATH, label: "Workspace" },
                  { href: "/organization", label: "Organization" },
                ]
              : [],
          },
        ]
      : []),
  ];
  // The Projects entry opens the area, whose own nav lists its pages.
  const panelHrefs = canUseProductTabs
    ? [PROJECTS_PATHS.projects, PROJECTS_PATHS.library, PROJECTS_PATHS.tasks]
    : [];
  return { entries, panelHrefs: panelHrefs.filter(isReachable) };
};

/**
 * What the nav the viewer is on lists, in its order and under its gates, for the palette's commands and result types.
 * `isLoading` holds while permissions and entitlements that hide entries load, so nothing shows and then goes.
 */
export const useReachableNav = (): {
  entries: ReachableEntry[];
  /** Every page the nav reaches: the entries, their links, and the pages their panels list. */
  hrefs: Set<string>;
  isLoading: boolean;
} => {
  const hasProductTabs = useHasProductTabs();
  const lead = useLeadEntries();
  const amp = useAmpEntries();
  const classic = useClassicEntries();
  const projectsAccess = useProjectsAccess();
  const agentsOnly = useIsAgentsOnlyWorkspace();
  const configureAgents = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const pages = hasProductTabs ? amp : classic;
  const entries = [...lead, ...pages.entries];
  return {
    entries,
    hrefs: new Set([
      ...entries.flatMap((entry) => [
        entry.href,
        ...entry.links.map((link) => link.href),
      ]),
      ...pages.panelHrefs,
    ]),
    isLoading:
      projectsAccess.isLoading ||
      agentsOnly.isLoading ||
      configureAgents.isLoading,
  };
};
